import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Label } from "@/components/ui/label";
import { cellState, nextTweak, sourceLabel } from "@/lib/access";
import { PermCell, PermFilters, PermLegend, PermTable, usePermFilter } from "./PermTable";

/** Per-user tweaks on top of what the role + audiences already give.
 *  Fetches the baseline for the chosen role/organization so the editor shows
 *  exactly what the person will end up with. */
export default function UserPermissionEditor({ catalog, role, organizationId, grants, revokes, onChange, testid = "user-permission-editor" }) {
  const filter = usePermFilter(catalog);
  const [baseline, setBaseline] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!role) return;
    setLoading(true);
    const org = organizationId && organizationId !== "none" ? organizationId : "";
    api.get(`/permissions/baseline?role=${encodeURIComponent(role)}&organization_id=${encodeURIComponent(org)}`)
      .then((r) => setBaseline(r.data)).catch(() => setBaseline(null)).finally(() => setLoading(false));
  }, [role, organizationId]);

  const base = useMemo(() => new Set(baseline?.effective || []), [baseline]);
  const audLabels = useMemo(() => Object.fromEntries((baseline?.audiences || []).map((a) => [a.audience_id, a.label])), [baseline]);
  const g = grants || [], r = revokes || [];
  const stateOf = (perm) => cellState({ inherited: base.has(perm), granted: g.includes(perm), revoked: r.includes(perm) });
  const click = (perm) => {
    const t = nextTweak(stateOf(perm));
    onChange({
      custom_permissions: t.granted ? [...new Set([...g, perm])] : g.filter((p) => p !== perm),
      revoked_permissions: t.revoked ? [...new Set([...r, perm])] : r.filter((p) => p !== perm),
    });
  };
  const effective = new Set([...base, ...g].filter((p) => !r.includes(p))).size;

  return (
    <div className="space-y-2 pt-1" data-testid={testid}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Label>Permissions <span className="font-normal text-slate-400">· {effective} effective · +{g.length} granted · −{r.length} revoked</span></Label>
        {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-300" />}
      </div>
      <p className="text-[11px] text-slate-500" data-testid={`${testid}-baseline`}>
        {baseline?.audiences?.length
          ? <>Defaults from <span className="font-medium text-slate-700">{baseline.audiences.map((a) => a.label).join(", ")}</span>{baseline.partner_type ? ` (${baseline.partner_type})` : ""}, adjusted by the role.</>
          : "This role is in no audience — only the role's own grants apply."}
        {" "}Click a cell to grant an extra or revoke something inherited.
      </p>
      <PermLegend />
      <PermFilters filter={filter} testid={`${testid}-filter`} />
      <PermTable groups={filter.shown} maxHeight="max-h-72" testid={`${testid}-table`}
        columns={[{ id: "user", label: "This person", sub: "click to change" }]}
        renderCell={(perm) => {
          const st = stateOf(perm);
          const from = (baseline?.sources?.[perm] || []).map((s) => sourceLabel(s, audLabels));
          return (
            <PermCell state={st} onClick={() => click(perm)} testid={`perm-${perm}`}
              title={st === "inherited" && from.length ? `From ${from.join(", ")} — click to revoke` : undefined} />
          );
        }} />
    </div>
  );
}
